'use client';

import { DateTime } from 'luxon';
import { useEffect, type ReactNode, useState } from 'react';
import { cn, tv } from '../_lib/utils';
import { Author, type AuthorProps } from '../Author';
import { Text } from '../Text';
import { Typography } from '../Typography';
import { Container } from '../Container';
import { LabelLink, LabelLinkProps } from '../LabelLink';
import { Button } from '../Inputs/Button';
import { IconResolver } from '../IconResolver';
import { Themes } from '../Theme';

const styles = tv({
	slots: {
		base: '',
		chip: 'py-1 flex items-center first:opacity-100 last:after:hidden after:bg-current after:mx-1.5 after:h-[0.1875rem] after:w-[0.1875rem] after:rounded-full',
	},
	variants: {
		layout: {
			page: {
				base: '',
			},
			popup: {
				base: 'pb-responsive-xl',
			},
		},
	},
});

export type BlogHeroProps = {
	author?: AuthorProps;
	className?: string;
	description?: ReactNode;
	startDateTime?: string;
	endDateTime?: string;
	label?: Pick<LabelLinkProps, 'copy' | 'href' | 'icon'>;
	location?: string;
	layout?: 'page' | 'popup';
	meta?: string[];
	title?: string;
	showShareButton?: boolean;
	linkCopiedText?: string;
	linkCopyFailedText?: string;
	theme?: Themes;
	button?: ReactNode;
};

export function ContentHero(props: BlogHeroProps) {
	const layout = props.layout ?? 'page';
	const showShareButton = props.showShareButton ?? true;

	const { base, chip } = styles({ layout });

	const [time, setTime] = useState<ReactNode | null>(null);
	const [copyUrlActive, setCopyUrlActive] = useState(false);
	const [copyUrlMessage, setCopyUrlMessage] = useState('');

	const linkCopiedText = props.linkCopiedText ?? 'Link copied to clipboard';
	const linkCopyFailedText = props.linkCopyFailedText ?? 'Oh no, something went wrong, please try again or copy the URL manually!';

	function copyUrlToClipboard() {
		if (!navigator.clipboard) {
			return;
		}

		navigator.clipboard.writeText(window.location.href).then(
			() => {
				setCopyUrlActive(true);
				setCopyUrlMessage(linkCopiedText);
			},
			() => {
				setCopyUrlActive(true);
				setCopyUrlMessage(linkCopyFailedText);
			},
		);
	}

	useEffect(() => {
		if (copyUrlActive) {
			setTimeout(() => {
				setCopyUrlActive(false);
			}, 1200);
		}
	}, [copyUrlActive]);

	useEffect(() => {
		const startDateFormatted = props.startDateTime && DateTime.fromISO(props.startDateTime, { setZone: true });
		const endDateFormatted = props.endDateTime && DateTime.fromISO(props.endDateTime, { setZone: true });

		if (startDateFormatted) {
			if (startDateFormatted.toFormat('t') !== '00:00') {
				setTime(<time dateTime={props.startDateTime}>{startDateFormatted.toFormat('d MMMM y')}</time>);
			} else if (
				!endDateFormatted ||
				(endDateFormatted && endDateFormatted.toFormat('d MMMM y') === startDateFormatted.toFormat('d MMMM y'))
			) {
				setTime(<time dateTime={props.startDateTime}>{startDateFormatted.toFormat('d MMMM y')}</time>);
			} else if (endDateFormatted && endDateFormatted.toFormat('d MMMM y') !== startDateFormatted.toFormat('d MMMM y')) {
				setTime(
					<time dateTime={props.startDateTime}>
						{startDateFormatted.toFormat('d')} - {endDateFormatted.toFormat('d MMMM y')}
					</time>,
				);
			}
		}
	}, [props.startDateTime, props.endDateTime]);

	let authorMeta: ReactNode = props.author?.meta;

	if (time !== null || props.location !== undefined || props.meta) {
		authorMeta = (
			<Text as='ul' className={`text-text-secondary flex flex-wrap`} styleType='chip-sm'>
				{time && <li className={chip()}>{time}</li>}
				{props.location && <li className={chip()}>{props.location}</li>}
				{props.meta?.map((item, index) => (
					<li className={chip()} key={`${item}-${index}-content-hero-meta`}>
						{item}
					</li>
				))}
			</Text>
		);
	}

	return (
		<article className={base({ className: props.className })} data-component='ContentHero' data-theme={props.theme}>
			<Container className='flex flex-col gap-responsive-md' size='editorial-wide'>
				<div className='flex flex-col gap-4'>
					{props.label && <LabelLink {...props.label} size='md' styleType='brand' />}

					{props.title && (
						<Text as='h1' styleType='heading-lg'>
							{props.title}
						</Text>
					)}
				</div>

				{props.description && <Typography styleType='body-lg'>{props.description}</Typography>}

				<div className='flex flex-col gap-4'>
					{props.author ? <Author image={props.author.image} name={props.author.name} meta={authorMeta} /> : authorMeta}
					<div className='flex gap-4 items-center'>
						{props.button && <div className='flex justify-start'>{props.button}</div>}

						{showShareButton && (
							<div className={`relative flex-none ml-auto`}>
								<Button
									aria-labelledby='share-copy-url-label'
									parent='ContentHero'
									aria-label={'Copy page url'}
									iconOnly
									styleSize='medium'
									styleType='tertiary'
									onClick={copyUrlToClipboard}
								>
									<IconResolver icon='export' className='w-3.5 h-3.5' />
								</Button>

								<Text
									as='div'
									aria-hidden={!copyUrlActive}
									tabIndex={copyUrlActive ? 0 : -1}
									className={cn(
										`absolute bottom-[100%] max-w-[20rem] opacity-0 pointer-events-none transition-all duration-slow ease-smooth w-24 text-center max-lg:right-0 lg:left-1/2 lg:-translate-x-1/2`,
										copyUrlActive ? 'opacity-100 pointer-events-auto -translate-y-2' : '',
									)}
									id='share-copy-url-label'
									role='tooltip'
									styleType='body-xs-medium'
								>
									{copyUrlMessage}
								</Text>
							</div>
						)}
					</div>
				</div>
			</Container>
		</article>
	);
}
